import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { ClientLogoutReason } from './services/auth/authService';
import ServiceUnavailableModal from './components/common/ServiceUnavailableModal';
import { subscribeApiUiEvents } from './services/api/apiEvents';
import { ApprovedRoute } from './auth/ApprovedRoute';
import './App.css';

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const DoctorSignUpPage = lazy(() => import('./pages/auth/DoctorSignUpPage'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const EditAvailabilityPage = lazy(() => import('./pages/profile/EditAvailabilityPage'));
const DoctorOnboardingPage = lazy(() => import('./pages/doctor-onboarding/DoctorOnboardingPage'));
const DoctorReviewStatusPage = lazy(() => import('./pages/doctor-onboarding/DoctorReviewStatusPage'));
const AIRequestsPage = lazy(() => import('./pages/ai-requests/AIRequestsPage'));
const ConsultationDetailsPage = lazy(() => import('./pages/ai-requests/ConsultationDetailsPage'));
const AppointmentsPage = lazy(() => import('./pages/appointments/AppointmentsPage'));
const EditAppointmentPage = lazy(() => import('./pages/appointments/EditAppointmentPage'));
const AppointmentDetailPage = lazy(() => import('./pages/appointments/AppointmentDetailPage'));
const VideoConferencePage = lazy(() => import('./pages/video-conference/VideoConferencePage'));
const PatientsPage = lazy(() => import('./pages/patients/PatientsPage'));
const PatientDetailPage = lazy(() => import('./pages/patients/PatientDetailPage'));
const PatientRecordLayout = lazy(() => import('./pages/patients/PatientRecordLayout'));
const PatientAppointmentsPage = lazy(() => import('./pages/patients/PatientAppointmentsPage'));
const PatientDietPlansPage = lazy(() => import('./pages/patients/PatientDietPlansPage'));
const PatientNutritionProfilePage = lazy(() => import('./pages/patients/PatientNutritionProfilePage'));
const PatientInsightsPage = lazy(() => import('./pages/patients/PatientInsightsPage'));
const PatientMessagesPage = lazy(() => import('./pages/patients/PatientMessagesPage'));
const CreatePatientPage = lazy(() => import('./pages/patients/CreatePatientPage'));
const DietPlanDetailPage = lazy(() => import('./pages/patients/DietPlanDetailPage'));
const RequirementsConversationPage = lazy(() => import('./pages/patients/RequirementsConversationPage'));
const MealPlanListPage = lazy(() => import('./pages/patients/MealPlanListPage'));
const MealPlanCreatePage = lazy(() => import('./pages/patients/MealPlanCreatePage'));
const MealPlanViewPage = lazy(() => import('./pages/patients/MealPlanViewPage'));
const MealPlanEditPage = lazy(() => import('./pages/patients/MealPlanEditPage'));
const MealPlanStandaloneViewPage = lazy(() => import('./pages/patients/MealPlanStandaloneViewPage'));
const MealPlanVersionViewPage = lazy(() => import('./pages/patients/MealPlanVersionViewPage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));

const RouteFallback: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--zivo-color-text-muted)',
    }}
  >
    Loading…
  </div>
);

// Protected Route Component — uses AuthProvider for bootstrap + session truth
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--zivo-color-text-muted)',
        }}
      >
        Loading…
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

function App() {
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceModalTitle, setServiceModalTitle] = useState('Service unavailable');
  const [serviceModalMessage, setServiceModalMessage] = useState('Network is down or service is not reachable. Please try again.');
  const [serviceModalRequiresRelogin, setServiceModalRequiresRelogin] = useState(false);
  const [serviceModalActionLabel, setServiceModalActionLabel] = useState<string | undefined>();
  const [serviceModalOnAction, setServiceModalOnAction] = useState<(() => void) | undefined>();

  const { logout } = useAuth();

  useEffect(() => {
    const unsubscribe = subscribeApiUiEvents((evt) => {
      const titleFromEvent =
        evt.title ??
        (evt.type === 'service_unavailable'
          ? 'Service unavailable'
          : evt.type === 'insufficient_wallet_balance'
            ? 'Wallet balance'
            : evt.type === 'subscription_required'
              ? 'Subscription required'
              : evt.type === 'rate_limited'
                ? 'Too many requests'
                : 'Session issue');
      setServiceModalTitle(titleFromEvent);
      setServiceModalMessage(typeof evt.message === 'string' ? evt.message : JSON.stringify(evt.message));
      setServiceModalRequiresRelogin(evt.type === 'session_issue');
      if (evt.type === 'insufficient_wallet_balance' || evt.type === 'subscription_required') {
        setServiceModalActionLabel(evt.type === 'subscription_required' ? 'Go to Billing' : 'Go to Wallet');
        setServiceModalOnAction(() => () => {
          setServiceModalOpen(false);
          window.location.href = '/billing';
        });
      } else {
        setServiceModalActionLabel(undefined);
        setServiceModalOnAction(undefined);
      }
      setServiceModalOpen(true);
    });
    return () => unsubscribe();
  }, []);

  return (
    <Router>
      <ServiceUnavailableModal
        open={serviceModalOpen}
        title={serviceModalTitle}
        message={serviceModalMessage}
        requiresRelogin={serviceModalRequiresRelogin}
        actionLabel={serviceModalActionLabel}
        onAction={serviceModalOnAction}
        onRetry={() => {
          setServiceModalOpen(false);
        }}
        onRelogin={() => {
          setServiceModalOpen(false);
          void (async () => {
            await logout(ClientLogoutReason.ModalRelogin);
            window.location.replace('/login?error=session_expired');
          })();
        }}
        onClose={() => {
          setServiceModalOpen(false);
        }}
      />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/doctor-signup" element={<DoctorSignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={
              <ApprovedRoute>
                <Dashboard />
              </ApprovedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ApprovedRoute>
                <ProfilePage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ApprovedRoute>
                <BillingPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/doctor-onboarding"
            element={
              <ProtectedRoute>
                <DoctorOnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor-review-status"
            element={
              <ProtectedRoute>
                <DoctorReviewStatusPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/availability"
            element={
              <ApprovedRoute>
                <EditAvailabilityPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/ai-requests"
            element={
              <ApprovedRoute>
                <AIRequestsPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/ai-requests/:id"
            element={
              <ApprovedRoute>
                <ConsultationDetailsPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ApprovedRoute>
                <AppointmentsPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/appointments/:id"
            element={
              <ApprovedRoute>
                <AppointmentDetailPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/appointments/:id/edit"
            element={
              <ApprovedRoute>
                <EditAppointmentPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/video-conference/:appointmentId"
            element={
              <ApprovedRoute>
                <VideoConferencePage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/patients"
            element={
              <ApprovedRoute>
                <PatientsPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/patients/create"
            element={
              <ApprovedRoute>
                <CreatePatientPage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/patients/:patientId/diet-plan/:goalId/daily-meals/new"
            element={
              <ApprovedRoute>
                <MealPlanCreatePage />
              </ApprovedRoute>
            }
          />
          <Route
            path="/patients/:patientId"
            element={
              <ApprovedRoute>
                <PatientRecordLayout />
              </ApprovedRoute>
            }
          >
            <Route index element={<PatientDetailPage />} />
            <Route path="appointments" element={<PatientAppointmentsPage />} />
            <Route path="diet-plans" element={<PatientDietPlansPage />} />
            <Route path="nutrition-profile" element={<PatientNutritionProfilePage />} />
            <Route path="insights" element={<PatientInsightsPage />} />
            <Route path="messages" element={<PatientMessagesPage />} />
            <Route path="diet-plan/:goalId/daily-meals/:planId/edit/:step?" element={<MealPlanEditPage />} />
            <Route path="diet-plan/:goalId/daily-meals/:planId" element={<MealPlanViewPage />} />
            <Route path="diet-plan/:goalId/daily-meals" element={<MealPlanListPage />} />
            <Route path="diet-plan/:goalId/conversation" element={<RequirementsConversationPage />} />
            <Route path="diet-plan/:goalId/:section" element={<DietPlanDetailPage />} />
            <Route path="diet-plan/:goalId" element={<DietPlanDetailPage />} />
            <Route path="daily-meals-plan/:planId/version" element={<MealPlanVersionViewPage />} />
            <Route path="daily-meals-plan/:planId" element={<MealPlanStandaloneViewPage />} />
          </Route>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
